/* eslint-disable array-bracket-newline */
import { useEffect, useState } from 'react';
import { useAccount, useNetwork } from 'wagmi';
import { subDays, subWeeks, subMonths, format, isBefore } from 'date-fns';
import {
  IDexTrade,
  ITicker,
  IWalletInfoChartItem,
  Network,
} from '@kattana-io/common';
// import Image from '../../../atoms/Image';
import Text from '../../../../atoms/Text';
import ChartComp from '../../../Info/ChartComponent';
// import Button from '../../../molecules/Button';
import PairIcons from '../../../../molecules/PairIcons';
import { decorateExchangeName } from '../../../../helpers/decorators';
import { WalletAPI } from '../../../../store/createStore';
import { formatUSD } from '../../../Modal/TickersModal/helpers';
import { onTxScan } from '../../../../helpers/addressUtils';
import useCollectTickers from '../../../DexTrades/useCollectTickers';
import useTicker from '../../../../hooks/useTicker';
import Loader from '../../../common/Loader';

// import dexIcon from '../../../assets/images/dex_logo.svg';
// import cexIcon from '../../../assets/images/cex_logo.svg';
// import shareIcon from '../../../assets/images/share.svg';

import './styles.scss';

type tickersMap = Record<string, ITicker>;

interface IChartEntity {
  day: string;
  reserve_usd: number;
}

const statsDefaultValue = {
  totalTrades: {
    num: 0,
    value: 0,
  },
  totalBuys: {
    num: 0,
    value: 0,
  },
  totalSells: {
    num: 0,
    value: 0,
  },
  pnl: {
    num: 0,
    value: 0,
  },
  totalFees: 0,
};

// enum Kind {
//   DEX,
//   CEX,
// }

enum ETimeframe {
  DAY = '24h',
  WEEK = 'Week',
  MONTH = 'Month',
  // ALL = 'All time',
}

const timeframes = [
  ETimeframe.DAY,
  ETimeframe.WEEK,
  ETimeframe.MONTH,
  // ETimeframe.ALL,
];

interface IStatistics {
  totalTrades: {
    num: number;
    value: number;
  };
  totalBuys: {
    num: number;
    value: number;
  };
  totalSells: {
    num: number;
    value: number;
  };
  pnl: {
    num: number;
    value: number;
  };
  totalFees: number;
}

const getDateFromTimeframe = (timeframe: ETimeframe) => {
  const date = Date.now();
  switch (timeframe) {
  case ETimeframe.DAY:
    return subDays(date, 1);
  case ETimeframe.WEEK:
    return subWeeks(date, 1);
  case ETimeframe.MONTH:
    return subMonths(date, 1);
  default:
    return subMonths(date, 2);
  }
};

const getRequestTimeframe = (
  timeframe: ETimeframe
): 'day' | 'week' | 'month' => {
  switch (timeframe) {
  case ETimeframe.DAY:
    return 'day';
  case ETimeframe.WEEK:
    return 'week';
  default:
    return 'month';
  }
};

const calcTradesInfo = (trades: IDexTrade[], tickers: tickersMap) =>
  trades.reduce(
    (cons, trade) => {
      const key = `${trade.chain}-${trade.pair}`;

      const flipped = tickers[key]?.flipped;
      const buy = flipped ? !trade.buy : trade.buy;
      return {
        buyUsd: buy ? cons.buyUsd + +trade.value_usd : cons.buyUsd,
        sellUsd: buy ? cons.sellUsd : cons.sellUsd + +trade.value_usd,
        buyCount: buy ? cons.buyCount + 1 : cons.buyCount,
        sellCount: buy ? cons.sellCount : cons.sellCount + 1,
        totalUsd: cons.totalUsd + +trade.value_usd,
      };
    },
    { buyUsd: 0, sellUsd: 0, buyCount: 0, sellCount: 0, totalUsd: 0 }
  );

const calcProfit = (trades: IDexTrade[], tickers: tickersMap) =>
  trades.reduce((prev: number, trade: IDexTrade) => {
    const key = `${trade.chain}-${trade.pair}`;

    const flipped = tickers[key]?.flipped;
    const buy = flipped ? !trade.buy : trade.buy;
    if (buy) {
      return prev - Number(trade.value_usd);
    } else {
      return prev + Number(trade.value_usd);
    }
  }, 0);

const WalletStatistics = () => {
  const [timeframe, setTimeframe] = useState(ETimeframe.DAY);
  // const [kind, setKind] = useState(Kind.DEX);
  const [loadingTrades, setLoadingTrades] = useState(true);
  const [dexTrades, setDexTrades] = useState<IDexTrade[]>([]);
  const [loadingChart, setLoadingChart] = useState(true);
  const [chartData, setChartData] = useState<IWalletInfoChartItem[]>([]);
  const [data, setData] = useState<{
    trades: IDexTrade[];
    stats: IStatistics;
    chart: IChartEntity[];
    chartTotal: number;
  }>({ trades: [], stats: statsDefaultValue, chart: [], chartTotal: 0 });

  const { chain } = useNetwork();
  const { address: wallet } = useAccount();

  const { tickers, loadingTickers } = useCollectTickers(dexTrades);

  useEffect(() => {
    const controller = new AbortController();
    if (wallet && chain) {
      setLoadingTrades(true);
      const network = Network.fromChainId(chain.id);
      WalletAPI.walletPeriodDexTrades(
        network.getSymbol(),
        wallet,
        getRequestTimeframe(timeframe),
        controller
      ).then((data) => {
        if (data) {
          const trades = Array.from(
            data
              .reduce(
                (cons, trade) =>
                  cons.set(`${trade.tx}-${trade.order}-${trade.date}`, trade),
                new Map()
              )
              .values()
          );
          setDexTrades(trades);
        } else {
          setDexTrades([]);
        }
        setLoadingTrades(false);
      });
    }
    return () => controller.abort();
  }, [wallet, chain, timeframe]);

  useEffect(() => {
    const controller = new AbortController();
    if (wallet && chain) {
      setLoadingChart(true);
      const network = Network.fromChainId(chain.id);
      WalletAPI.walletInfoChart(
        network.getSymbol(),
        wallet,
        controller.signal
      ).then((data) => {
        if (data) {
          setChartData(data);
        }
        setLoadingChart(false);
      });
    }

    return () => controller.abort();
  }, [wallet, chain]);

  useEffect(() => {
    const data: IChartEntity[] = chartData.map((item) => ({
      day: item.timestamp,
      reserve_usd: item.value_usd,
    }));
    const date = getDateFromTimeframe(timeframe);
    const chart = data.filter((item) => isBefore(date, new Date(item.day)));
    const chartTotal = chart[chart.length - 1]?.reserve_usd || 0;
    setData((prev) => ({ ...prev, chart, chartTotal }));
  }, [timeframe, chartData]);

  useEffect(() => {
    const { buyUsd, sellUsd, totalUsd, buyCount, sellCount } = calcTradesInfo(
      dexTrades,
      tickers
    );

    const pnl = buyUsd ? (sellUsd - buyUsd) / buyUsd : 0;

    const profit = calcProfit(dexTrades, tickers);

    const statistcs: IStatistics = {
      totalTrades: {
        num: buyCount + sellCount,
        value: totalUsd,
      },
      totalBuys: {
        num: buyCount,
        value: buyUsd,
      },
      totalSells: {
        num: sellCount,
        value: sellUsd,
      },
      pnl: {
        num: pnl * 100,
        value: pnl * totalUsd,
      },
      totalFees: profit,
    };

    setData((prev) => ({
      ...prev,
      trades: dexTrades,
      stats: statistcs,
    }));
  }, [tickers]);

  // const onShare = () => {
  //   return;
  // };

  return (
    <div className='wallet_statistics select_wallet__appear_box'>
      {/* <div className='wallet_statistics__tabs'>
        <div
          className={`wallet_statistics__tabs__item ${
            kind === Kind.DEX ? 'wallet_statistics__tabs__item--active' : ''
          }`}
          onClick={() => setKind(Kind.DEX)}
        >
          <Image src={dexIcon} size='s' />
          <Text size={14}>DEX</Text>
        </div>
        <div
          className={`wallet_statistics__tabs__item ${
            kind === Kind.CEX ? 'wallet_statistics__tabs__item--active' : ''
          }`}
          onClick={() => setKind(Kind.CEX)}
        >
          <Image src={cexIcon} size='s' />
          <Text size={14}>CEX</Text>
        </div>
      </div> */}
      <div>
        <Text size={18} className='text--grey-200'>
          {formatUSD(data.chartTotal)}
        </Text>
        <div style={{ height: '96px', width: '100%' }}>
          {loadingChart ? (
            <div className='centrify'>
              <Loader />
            </div>
          ) : !chartData.length ? (
            <div className='centrify'>
              <Text size={14} className='text--grey-400'>
                No data
              </Text>
            </div>
          ) : (
            <ChartComp data={data.chart} height='96px' width='100%' hideAxis />
          )}
        </div>
        <div className='wallet_statistics__timeframes'>
          {timeframes.map((item) => (
            <Text
              key={item}
              size={12}
              className={`wallet_statistics__timeframes__item ${
                item === timeframe
                  ? 'wallet_statistics__timeframes__item--active'
                  : ''
              }`}
              onClick={() => setTimeframe(item)}
            >
              {item}
            </Text>
          ))}
        </div>
      </div>
      <div className='wallet_statistics__stats'>
        <div className='wallet_statistics__stats__main_list'>
          <div className='wallet_statistics__stats__main_list__item'>
            <Text size={12} className='text--grey-600 text--weight-600'>
              Total trades:
            </Text>
            <div className='wallet_statistics__stats__main_list__item__value'>
              <Text size={14}>{data.stats.totalTrades.num}</Text>
              <Text size={14}>${data.stats.totalTrades.value.toFixed(2)}</Text>
            </div>
          </div>
          <div className='wallet_statistics__stats__main_list__item'>
            <Text size={12} className='text--grey-600 text--weight-600'>
              Total buys:
            </Text>
            <div className='wallet_statistics__stats__main_list__item__value'>
              <Text size={14}>{data.stats.totalBuys.num}</Text>
              <Text size={14}>${data.stats.totalBuys.value.toFixed(2)}</Text>
            </div>
          </div>
          <div className='wallet_statistics__stats__main_list__item'>
            <Text size={12} className='text--grey-600 text--weight-600'>
              Total sells:
            </Text>
            <div className='wallet_statistics__stats__main_list__item__value'>
              <Text size={14}>{data.stats.totalSells.num}</Text>
              <Text size={14}>${data.stats.totalSells.value.toFixed(2)}</Text>
            </div>
          </div>
          <div className='wallet_statistics__stats__main_list__item'>
            <Text size={12} className='text--grey-600 text--weight-600'>
              PnL:
            </Text>
            <div
              className={`wallet_statistics__stats__main_list__item__value ${
                data.stats.pnl.num < 0 ? 'text--red-600' : 'text--green-600'
              }`}
            >
              <Text size={14}>
                {data.stats.pnl.num > 0 ? '+' : ''}
                {data.stats.pnl.num.toFixed(2)}%
              </Text>
              <Text size={14}>${data.stats.pnl.value.toFixed(2)}</Text>
            </div>
          </div>
        </div>
        <div className='wallet_statistics__stats__fees'>
          <Text size={12} className='text--grey-600 text--weight-600'>
            Profit:
          </Text>
          <Text size={14} className='text--grey-400'>
            ${data.stats.totalFees.toFixed(2)}
          </Text>
        </div>
        {/* <div className='wallet_statistics__stats__btn'>
          <Button
            left_image={shareIcon}
            action={onShare}
            size='s'
            variant='primary'
            text='Share'
          />
        </div> */}
      </div>
      <div className='wallet_statistics__pairs_table'>
        {loadingTickers || loadingTrades ? (
          <div className='centrify'>
            <Loader />
          </div>
        ) : !data.trades.length ? (
          <div className='centrify'>
            <Text size={14} className='text--grey-400'>
              No data
            </Text>
          </div>
        ) : (
          data.trades.map((trade) => {
            const key = `${trade.chain}-${trade.pair}`;

            if (!tickers[key]) {
              return null;
            }

            const ticker = useTicker(tickers[key]);

            const value = `${(+trade.value_usd / trade.price_a_usd).toFixed(
              1
            )}${ticker.base}($${Number(trade.value_usd).toFixed(1)})`;
            return (
              <div
                key={`${trade.tx}-${trade.order}-${trade.date}`}
                className='wallet_statistics__pairs_table__row'
              >
                <Text
                  size={10}
                  className='wallet_statistics__pairs_table__row__number'
                >
                  {format(new Date(trade.date), 'dd MM yy')}
                </Text>
                <PairIcons {...{ ...ticker, pair: trade.pair }} vertical />
                <div className='wallet_statistics__pairs_table__row__pair_title'>
                  <Text size={14} className='text--grey-200 text--weight-500'>
                    {ticker.name}
                  </Text>
                  <Text size={12} className='text--grey-600'>
                    {decorateExchangeName(ticker.exchange, ticker.chain)}
                  </Text>
                </div>
                <div className='wallet_statistics__pairs_table__row__trade'>
                  <Text
                    size={14}
                    className={`text--weight-500 ${
                      (tickers[key].flipped ? !trade.buy : trade.buy)
                        ? 'text--green-600'
                        : 'text--red-600'
                    }`}
                    style={{ title: value }}
                  >
                    {value}
                  </Text>
                  <Text size={12} className='text--grey-600'>
                    {Network.fromChain(trade.chain).getTitle()}
                  </Text>
                </div>
                <Text
                  size={12}
                  className='wallet_statistics__pairs_table__row__tx'
                  onClick={() => onTxScan(trade.tx, trade.chain)}
                >
                  TX
                </Text>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default WalletStatistics;
